import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check } from "lucide-react";

// Marketing respondents invite a leader; leadership respondents invite a marketer.
export function CounterpartInvite({ respondentId, pathway }) {
  const isMarketing = pathway === "marketing";
  const headline = isMarketing
    ? "Think your CEO would answer differently?"
    : "Think your marketing team sees things differently?";
  const ctaLabel = isMarketing ? "Invite a leader" : "Invite marketing";
  const roleDefault = isMarketing ? "CEO / Business Leader" : "Marketing Lead";

  const [form, setForm] = useState({ name: "", email: "", role: roleDefault, note: "" });
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      await api.inviteCounterpart(respondentId, form);
      setDone(true);
      toast.success("Invitation captured.");
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-sm border border-neutral-800 bg-black p-8 text-center text-white sm:p-12">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#1408F7]">
          <Check className="h-6 w-6 text-white" />
        </div>
        <h3 className="mt-6 text-xl font-semibold">Invitation captured.</h3>
        <p className="mt-2 text-neutral-400">
          We&apos;ve recorded your invitation to {form.name || form.email}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-neutral-800 bg-black p-8 text-white sm:p-12">
      <p className="tw-eyebrow text-neutral-500">Alignment</p>
      <h3 className="mt-5 text-2xl font-semibold tracking-tight">{headline}</h3>
      <p className="mt-3 max-w-xl text-neutral-400">
        Invite your counterpart to complete their diagnostic. We&apos;ll keep both results linked to your
        organisation for a future alignment view.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label className="text-neutral-300">Their name</Label>
          <Input
            value={form.name}
            onChange={set("name")}
            placeholder="Alex Morgan"
            className="mt-2 h-12 rounded-sm border-neutral-700 bg-neutral-900 text-white placeholder:text-neutral-600 focus-visible:ring-[#1408F7]"
            data-testid="counterpart-name-input"
          />
        </div>
        <div>
          <Label className="text-neutral-300">Their role</Label>
          <Input
            value={form.role}
            onChange={set("role")}
            className="mt-2 h-12 rounded-sm border-neutral-700 bg-neutral-900 text-white placeholder:text-neutral-600 focus-visible:ring-[#1408F7]"
            data-testid="counterpart-role-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-neutral-300">Their email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="alex@company.com"
            className="mt-2 h-12 rounded-sm border-neutral-700 bg-neutral-900 text-white placeholder:text-neutral-600 focus-visible:ring-[#1408F7]"
            data-testid="counterpart-email-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-neutral-300">Personal note (optional)</Label>
          <Textarea
            value={form.note}
            onChange={set("note")}
            placeholder="A short note to add context…"
            className="mt-2 rounded-sm border-neutral-700 bg-neutral-900 text-white placeholder:text-neutral-600 focus-visible:ring-[#1408F7]"
            data-testid="counterpart-note-input"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="tw-btn-primary tw-focus mt-8 rounded-sm px-8 py-3 text-sm font-semibold uppercase tracking-wide disabled:opacity-40"
        data-testid="counterpart-send-invite-button"
      >
        {saving ? "Sending…" : ctaLabel}
      </button>
    </form>
  );
}
